/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import get from 'lodash/get';
import { Placeholder } from 'sarsaparilla';

import PermitMap from 'ui-permit/src/shared/components/PermitMap';
import { useIsMounted } from 'ui-permit/shared-components/utils/hooks_utils';

export default function DivisionsMapContainer({
    permit,
    selectedDivision,
    permitType,
    onFullScreen,
    fitFeatures,
}) {
    const [lines, setLines] = useState(null);
    const [points, setPoints] = useState(null);
    const [polygons, setPolygons] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const isMounted = useIsMounted();
    const permitId = permit?.id;

    const fetchMapDrawData = async (id) => {
        try {
            const response = await axios.get(
                `/shared/${permitType}/data/${id}/features.json`,
                {
                    credentials: 'include',
                }
            );

            const json = response?.data ?? {};
            if (isMounted.current) {
                setLines(json.lines);
                setPoints(json.points);
                setPolygons(json.polygons);
            }
        } catch {
            // If fetch error occurs just catch it to not raise it, no big impacts
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!permitId) return;
        fetchMapDrawData(permitId);
    }, [permitId]);

    const selectedFeatureId = selectedDivision?.id || null;

    if (isLoading) {
        return <Placeholder width="100%" height="600px" />;
    }

    return (
        <PermitMap
            geoJsonCenterPoint={{
                geometry: {
                    coordinates: [
                        get(permit, 'longitude', 0),
                        get(permit, 'latitude', 0),
                    ],
                    type: 'Point',
                },
                properties: {
                    name: get(permit, 'name', ''),
                    type: 'Permit',
                },
                type: 'Feature',
            }}
            center={
                permit?.latitude && permit?.longitude
                    ? [permit.longitude, permit.latitude]
                    : [0, 0]
            }
            geoJsonLines={lines}
            geoJsonPoints={points}
            geoJsonPolygons={polygons}
            mapContainer="map"
            selectedFeatureId={selectedFeatureId}
            showPolygonLabels
            onFullScreen={onFullScreen}
            fitFeatures={fitFeatures}
        />
    );
}

DivisionsMapContainer.propTypes = {
    permit: PropTypes.object,
    selectedDivision: PropTypes.object,
    permitType: PropTypes.string,
    onFullScreen: PropTypes.func,
    fitFeatures: PropTypes.bool,
};
